/**
 * rest/client.ts — Client HTTP centralisé Assistia
 *
 * Adapté aux implémentations Gemini (S5.4-06/07) :
 *   - TokenManager.getToken() pour le Bearer
 *   - TokenManager.handle401() pour le refresh lock
 *   - CircuitBreaker.call() pour le fallback simulation
 */
import { AccessDeniedError, SecurityBlockedError } from './errors.js';
const REFRESH_ENDPOINT = '/api/v1/auth/refresh';
export class RestClient {
    opts;
    constructor(opts) {
        this.opts = opts;
    }
    // ── Méthode principale ────────────────────────────────────────────────────
    async request(method, path, options) {
        return this.opts.circuitBreaker.call(() => this._fetch(method, path, options.body, options.bearerOverride), options.fallback);
    }
    // ── Fetch avec auth + délégation 401 à TokenManager.handle401 ────────────
    async _fetch(method, path, body, bearerOverride) {
        const url = `${this.opts.baseUrl}${path}`;
        const headers = this._buildHeaders(bearerOverride);
        const res = await this._doFetch(url, method, headers, body);
        // 401 → déléguer le refresh lock à l'implémentation Gemini
        if (res.status === 401) {
            const requestConfig = { url, method, headers, data: body };
            const retried = await this.opts.tokenManager.handle401(REFRESH_ENDPOINT, (req) => fetch(req.url, {
                method: req.method,
                headers: { ...req.headers, ...this._buildHeaders() },
                body: req.data !== undefined ? JSON.stringify(req.data) : null,
            }).then(r => r.json()), requestConfig);
            return retried;
        }
        if (!res.ok) {
            if (res.status === 403) {
                let payload;
                try {
                    payload = await res.clone().json();
                }
                catch {
                    // ignore parsing error
                }
                // 6.8-01 : blocage guardrail (contenu) ≠ refus licence/auth.
                // Un message bloqué ne doit JAMAIS être réessayé tel quel.
                if (payload?.blocked === true) {
                    throw new SecurityBlockedError(payload.detail ?? 'BLOCKED');
                }
                if (payload?.error === 'access_denied') {
                    throw new AccessDeniedError(payload.feature ?? 'unknown', payload.message);
                }
                throw new AccessDeniedError('unknown', 'Access denied: Premium license required.');
            }
            const err = new Error(`HTTP ${res.status} on ${method} ${path}`);
            err.status = res.status;
            throw err;
        }
        if (res.status === 204)
            return undefined;
        return res.json();
    }
    _buildHeaders(bearerOverride) {
        const token = bearerOverride ?? this.opts.tokenManager.getToken();
        const headers = {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        };
        if (token)
            headers['Authorization'] = `Bearer ${token}`;
        return headers;
    }
    _doFetch(url, method, headers, body) {
        const controller = new AbortController();
        const timerId = setTimeout(() => controller.abort(), this.opts.timeoutMs);
        return fetch(url, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : null,
            signal: controller.signal,
        }).finally(() => clearTimeout(timerId));
    }
    /** Expose le token courant pour les appels multipart qui contournent RestClient */
    getToken() {
        return this.opts.tokenManager.getToken();
    }
    /** True en mode démo : les appels backend doivent être court-circuités. */
    isDemoMode() {
        return this.opts.circuitBreaker.isDemoMode();
    }
    // ── Helpers CRUD ──────────────────────────────────────────────────────────
    get(path, fallback) {
        return this.request('GET', path, { fallback });
    }
    post(path, body, fallback, opts) {
        const extra = opts?.bearerOverride !== undefined ? { bearerOverride: opts.bearerOverride } : {};
        return this.request('POST', path, { body, fallback, ...extra });
    }
    patch(path, body, fallback) {
        return this.request('PATCH', path, { body, fallback });
    }
    delete(path, fallback) {
        return this.request('DELETE', path, { fallback });
    }
}
//# sourceMappingURL=client.js.map